import {
  type BookingDto,
  type ClinicDto,
  LOCALES,
  type MeDto,
  type Page,
  type PractitionerDto,
  type ServiceDto,
  SLOT_INTERVALS,
} from "@clinic/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link, Navigate, NavLink, Outlet, useNavigate } from "react-router";
import { api } from "../api";
import { fieldErrors, hasFieldErrors } from "../forms";
import { formatDate, formatMoney, LanguageSwitcher, localDate, useT } from "../i18n";
import { activeMembership, canManage, useLogout, useMe, useSessionMutation } from "../session";
import { Alert, Badge, Button, Card, Field, FullPageSpinner, Logo, PageHeader, Select, Spinner, Textarea } from "../ui";

export function useRequiredMe(): MeDto {
  const me = useMe();
  // AppLayout renders children only once `me` is loaded and present.
  return me.data as MeDto;
}

export function useClinic() {
  return useQuery({ queryKey: ["clinic"], queryFn: () => api<ClinicDto>("GET", "/clinic"), staleTime: 60_000 });
}

export function AppLayout() {
  const me = useMe();
  const { t } = useT();
  const logout = useLogout();
  const navigate = useNavigate();
  const switchClinic = useSessionMutation((clinicId: string) =>
    api<MeDto>("POST", "/auth/active-clinic", { clinicId }),
  );

  if (me.isPending) return <FullPageSpinner />;
  if (me.isError) {
    return (
      <div className="mx-auto mt-24 max-w-md px-4">
        <Alert>{t("app.account_load_failed")}</Alert>
      </div>
    );
  }
  if (!me.data) return <Navigate to="/login" replace />;

  const current = activeMembership(me.data);
  const navItem = ({ isActive }: { isActive: boolean }) =>
    `block rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap ${isActive ? "bg-brand-50 text-brand-800" : "text-slate-600 hover:bg-slate-100"}`;

  return (
    <div className="min-h-screen md:flex">
      <aside className="border-b border-slate-200 bg-white md:min-h-screen md:w-64 md:border-r md:border-b-0">
        <div className="flex items-center justify-between gap-3 px-5 py-4 md:flex-col md:items-start">
          <Link to="/app">
            <Logo />
          </Link>
          <LanguageSwitcher />
        </div>
        {me.data.memberships.length > 1 ? (
          <div className="px-5 pb-3">
            <label htmlFor="clinic-switch" className="text-xs font-medium tracking-wide text-slate-500 uppercase">
              {t("nav.clinic")}
            </label>
            <select
              id="clinic-switch"
              className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm"
              value={me.data.activeClinicId ?? ""}
              onChange={(e) => switchClinic.mutate(e.target.value, { onSuccess: () => navigate("/app") })}
            >
              {me.data.memberships.map((m) => (
                <option key={m.clinicId} value={m.clinicId}>
                  {m.clinicName}
                </option>
              ))}
            </select>
          </div>
        ) : current ? (
          <p className="px-5 pb-1 text-sm font-medium text-slate-700">{current.clinicName}</p>
        ) : null}
        <p className="px-5 pb-3">
          <Link to="/app/clinics/new" className="text-xs font-medium text-brand-700 hover:underline">
            {t("clinics.new_link")}
          </Link>
        </p>
        <nav className="flex gap-1 overflow-x-auto px-3 pb-3 md:block md:space-y-1" aria-label={t("nav.main")}>
          <NavLink to="/app" end className={navItem}>
            {t("nav.overview")}
          </NavLink>
          <NavLink to="/app/bookings" className={navItem}>
            {t("nav.bookings")}
          </NavLink>
          <NavLink to="/app/practitioners" className={navItem}>
            {t("nav.practitioners")}
          </NavLink>
          <NavLink to="/app/services" className={navItem}>
            {t("nav.services")}
          </NavLink>
          {canManage(me.data) ? (
            <>
              <NavLink to="/app/insights" className={navItem}>
                {t("nav.insights")}
              </NavLink>
              <NavLink to="/app/team" className={navItem}>
                {t("nav.team")}
              </NavLink>
              <NavLink to="/app/settings" className={navItem}>
                {t("nav.settings")}
              </NavLink>
            </>
          ) : null}
          <NavLink to="/app/profile" className={navItem}>
            {t("nav.profile")}
          </NavLink>
        </nav>
        <div className="hidden px-5 py-4 md:block">
          <p className="truncate text-sm font-medium">{me.data.user.fullName}</p>
          <p className="truncate text-xs text-slate-500">{me.data.user.email}</p>
          <button
            type="button"
            className="mt-3 text-sm font-medium text-slate-600 hover:text-slate-900"
            onClick={() => logout.mutate(undefined, { onSettled: () => navigate("/login") })}
          >
            {t("nav.sign_out")}
          </button>
        </div>
      </aside>
      <main className="flex-1 px-4 py-8 md:px-10">
        <div className="mx-auto max-w-5xl">
          {current ? (
            <>
              <SubscriptionBanner />
              <Outlet />
            </>
          ) : (
            <Alert tone="info">{t("app.not_linked")}</Alert>
          )}
        </div>
      </main>
    </div>
  );
}

/** Tells staff when the trial is running out, payment is overdue, or the booking page is closed (CB-33). */
function SubscriptionBanner() {
  const { t } = useT();
  const clinic = useClinic();
  const c = clinic.data;
  if (!c) return null;
  const state = c.status === "suspended" ? "suspended" : (c.subscription?.state ?? "none");
  let message: string | null = null;
  let tone: "error" | "info" = "error";
  if (state === "trialing" && c.subscription?.trialEndsAt) {
    const days = Math.ceil((Date.parse(c.subscription.trialEndsAt) - Date.now()) / 86_400_000);
    tone = "info";
    message = days <= 1 ? t("sub.banner_trial_last_day") : t("sub.banner_trialing", { n: days });
  } else if (state === "past_due") message = t("sub.banner_past_due");
  else if (state === "expired" || state === "none") message = t("sub.banner_expired");
  else if (state === "cancelled") message = t("sub.banner_cancelled");
  else if (state === "suspended") message = t("sub.banner_suspended", { reason: "" }).trim();
  if (!message) return null;
  return (
    <div className="mb-6">
      <Alert tone={tone}>{message}</Alert>
    </div>
  );
}

export function OverviewPage() {
  const me = useRequiredMe();
  const { t } = useT();
  const clinic = useClinic();
  const services = useQuery({
    queryKey: ["services", "count"],
    queryFn: () => api<Page<ServiceDto>>("GET", "/services?limit=1"),
  });
  const practitioners = useQuery({
    queryKey: ["practitioners", "count"],
    queryFn: () => api<Page<PractitionerDto>>("GET", "/practitioners?limit=1"),
  });
  const today = localDate(Date.now(), clinic.data?.timezone);
  const bookings = useQuery({
    queryKey: ["bookings", "today", today],
    enabled: Boolean(clinic.data),
    queryFn: () => {
      const from = new Date(`${today}T00:00:00Z`);
      from.setUTCDate(from.getUTCDate() - 1);
      const to = new Date(`${today}T00:00:00Z`);
      to.setUTCDate(to.getUTCDate() + 2);
      return api<Page<BookingDto>>(
        "GET",
        `/bookings?from=${from.toISOString()}&to=${to.toISOString()}&status=confirmed&limit=100`,
      ).then((p) => p.items.filter((b) => localDate(b.startsAt, clinic.data?.timezone) === today).length);
    },
  });
  const membership = activeMembership(me);
  const roleKey = `app.role_${membership?.role ?? "practitioner"}` as const;
  const bookingUrl = clinic.data ? `${window.location.origin}/book/${clinic.data.slug}` : "";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("app.welcome", { name: me.user.fullName.split(" ")[0] ?? "" })}</h1>
        <p className="mt-1 text-slate-600">
          {membership?.clinicName} · {t(roleKey)}
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <p className="text-sm text-slate-500">{t("app.bookings_today")}</p>
          <p className="mt-2 text-3xl font-semibold">{bookings.data ?? <Spinner />}</p>
        </Card>
        <Card>
          <p className="text-sm text-slate-500">{t("app.practitioners")}</p>
          <p className="mt-2 text-3xl font-semibold">{practitioners.data ? practitioners.data.total : <Spinner />}</p>
        </Card>
        <Card>
          <p className="text-sm text-slate-500">{t("app.active_services")}</p>
          <p className="mt-2 text-3xl font-semibold">{services.data ? services.data.total : <Spinner />}</p>
        </Card>
      </div>
      {services.data?.total === 0 && canManage(me) ? (
        <Card>
          <h2 className="font-semibold">{t("app.get_started")}</h2>
          <p className="mt-1 text-sm text-slate-600">{t("app.get_started_services")}</p>
          <Link to="/app/services" className="mt-4 inline-block text-sm font-semibold text-brand-700 hover:underline">
            {t("app.add_first_service")}
          </Link>
        </Card>
      ) : practitioners.data?.total === 0 && canManage(me) ? (
        <Card>
          <h2 className="font-semibold">{t("app.get_started")}</h2>
          <p className="mt-1 text-sm text-slate-600">{t("app.get_started_practitioners")}</p>
          <Link
            to="/app/practitioners"
            className="mt-4 inline-block text-sm font-semibold text-brand-700 hover:underline"
          >
            {t("app.add_first_practitioner")}
          </Link>
        </Card>
      ) : null}
      {clinic.data ? (
        <Card>
          <h2 className="font-semibold">{t("app.booking_page")}</h2>
          <p className="mt-1 text-sm text-slate-600">{t("app.booking_page_text")}</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <code className="rounded-lg bg-slate-100 px-3 py-2 text-sm">{bookingUrl}</code>
            <a
              href={bookingUrl}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-semibold text-brand-700 hover:underline"
            >
              {t("app.open_booking_page")} ↗
            </a>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

export function ServicesPage() {
  const me = useRequiredMe();
  const { t, tError, locale } = useT();
  const qc = useQueryClient();
  const [showInactive, setShowInactive] = useState(false);
  const list = useQuery({
    queryKey: ["services", { showInactive }],
    queryFn: () => api<Page<ServiceDto>>("GET", `/services?limit=100&includeInactive=${showInactive}`),
  });
  const create = useMutation({
    mutationFn: (input: unknown) => api<ServiceDto>("POST", "/services", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["services"] }),
  });
  const toggle = useMutation({
    mutationFn: (s: ServiceDto) => api<ServiceDto>("PATCH", `/services/${s.id}`, { active: !s.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["services"] }),
  });
  const fields = fieldErrors(create.error);

  const onCreate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const v = Object.fromEntries(new FormData(form)) as Record<string, string>;
    create.mutate(
      {
        name: v.name,
        description: v.description ?? "",
        durationMinutes: Number(v.durationMinutes),
        priceCents: Math.round(Number(String(v.price).replace(",", ".")) * 100),
        currency: "DKK",
      },
      { onSuccess: () => form.reset() },
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t("services.title")} subtitle={t("services.subtitle")}>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          {t("services.show_inactive")}
        </label>
      </PageHeader>

      <Card className="p-0">
        {list.isPending ? (
          <div className="flex justify-center p-8 text-brand-700">
            <Spinner />
          </div>
        ) : list.isError ? (
          <div className="p-6">
            <Alert>{t("services.load_failed")}</Alert>
          </div>
        ) : list.data.items.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500">{t("services.empty")}</p>
        ) : (
          <ul className="divide-y divide-slate-200">
            {list.data.items.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-3 px-6 py-4">
                <div>
                  <p className={`font-medium ${s.active ? "" : "text-slate-400 line-through"}`}>{s.name}</p>
                  <p className="text-sm text-slate-500">
                    {t("common.minutes", { n: s.durationMinutes })} · {formatMoney(locale, s.priceCents, s.currency)}
                  </p>
                </div>
                {canManage(me) ? (
                  <Button
                    type="button"
                    variant="secondary"
                    busy={toggle.isPending && toggle.variables?.id === s.id}
                    onClick={() => toggle.mutate(s)}
                  >
                    {s.active ? t("services.deactivate") : t("services.activate")}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {canManage(me) ? (
        <Card>
          <h2 className="font-semibold">{t("services.add_title")}</h2>
          <form className="mt-4 grid gap-4 sm:grid-cols-2" onSubmit={onCreate}>
            {create.error && !hasFieldErrors(create.error) ? (
              <div className="sm:col-span-2">
                <Alert>{tError(create.error)}</Alert>
              </div>
            ) : null}
            <div className="sm:col-span-2">
              <Field
                label={t("services.name")}
                name="name"
                required
                placeholder={t("services.name_placeholder")}
                error={fields.name}
              />
            </div>
            <Field
              label={t("services.duration")}
              name="durationMinutes"
              type="number"
              min={5}
              max={1440}
              step={5}
              defaultValue={30}
              required
              error={fields.durationMinutes}
            />
            <Field
              label={t("services.price")}
              name="price"
              inputMode="decimal"
              defaultValue="0"
              required
              error={fields.priceCents}
            />
            <div className="sm:col-span-2">
              <Field label={t("services.description")} name="description" error={fields.description} />
            </div>
            <div className="sm:col-span-2">
              <Button busy={create.isPending}>{t("services.add_button")}</Button>
            </div>
          </form>
        </Card>
      ) : null}
    </div>
  );
}

export function SettingsPage() {
  const me = useRequiredMe();
  const { t, tError, locale } = useT();
  const qc = useQueryClient();
  const clinic = useClinic();
  const save = useMutation({
    mutationFn: (input: Partial<ClinicDto>) => api<ClinicDto>("PATCH", "/clinic", input),
    onSuccess: (data) => {
      qc.setQueryData(["clinic"], data);
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
  if (!canManage(me)) return <Navigate to="/app" replace />;
  if (clinic.isPending) return <Spinner />;
  if (clinic.isError) return <Alert>{t("settings.load_failed")}</Alert>;
  const fields = fieldErrors(save.error);
  const c = clinic.data;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t("settings.title")}</h1>
      <Card>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            const v = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
            save.mutate({
              name: v.name,
              timezone: v.timezone,
              defaultLocale: v.defaultLocale as ClinicDto["defaultLocale"],
              customerAccountsEnabled: v.customerAccountsEnabled === "on",
              listed: v.listed === "on",
              address: v.address ?? "",
              phone: v.phone ?? "",
              description: v.description ?? "",
              smsRemindersEnabled: v.smsRemindersEnabled === "on",
              slotIntervalMinutes: Number(v.slotIntervalMinutes),
              minNoticeMinutes: Number(v.minNoticeMinutes),
              maxAdvanceDays: Number(v.maxAdvanceDays),
              cancellationNoticeMinutes: Number(v.cancellationNoticeMinutes),
            });
          }}
        >
          {save.isSuccess ? <Alert tone="success">{t("settings.saved")}</Alert> : null}
          {save.error && !hasFieldErrors(save.error) ? <Alert>{tError(save.error)}</Alert> : null}
          <Field label={t("settings.clinic_name")} name="name" defaultValue={c.name} required error={fields.name} />
          <Field
            label={t("settings.timezone")}
            name="timezone"
            defaultValue={c.timezone}
            hint={t("settings.timezone_hint")}
            required
            error={fields.timezone}
          />
          <p className="text-sm text-slate-600">
            {t("settings.booking_page")}: <span className="font-mono">/book/{c.slug}</span>
          </p>
          <Select
            label={t("settings.default_language")}
            name="defaultLocale"
            defaultValue={c.defaultLocale}
            hint={t("settings.default_language_hint")}
            error={fields.defaultLocale}
          >
            {LOCALES.map((l) => (
              <option key={l} value={l}>
                {t(`lang.${l}`)}
              </option>
            ))}
          </Select>
          <label className="flex items-start gap-3 rounded-lg p-3 ring-1 ring-slate-200">
            <input
              type="checkbox"
              name="customerAccountsEnabled"
              defaultChecked={c.customerAccountsEnabled}
              className="mt-1"
            />
            <span>
              <span className="block text-sm font-medium">{t("settings.customer_accounts")}</span>
              <span className="block text-sm text-slate-500">{t("settings.customer_accounts_text")}</span>
            </span>
          </label>

          <h2 className="pt-2 font-semibold">{t("settings.public_profile")}</h2>
          <Field label={t("settings.address")} name="address" defaultValue={c.address} error={fields.address} />
          <Field label={t("settings.phone")} name="phone" type="tel" defaultValue={c.phone} error={fields.phone} />
          <Textarea
            label={t("settings.description")}
            name="description"
            rows={3}
            defaultValue={c.description}
            error={fields.description}
          />
          <label className="flex items-start gap-3 rounded-lg p-3 ring-1 ring-slate-200">
            <input type="checkbox" name="listed" defaultChecked={c.listed} className="mt-1" />
            <span>
              <span className="block text-sm font-medium">{t("settings.directory")}</span>
              <span className="block text-sm text-slate-500">{t("settings.directory_text")}</span>
            </span>
          </label>
          <label className="flex items-start gap-3 rounded-lg p-3 ring-1 ring-slate-200">
            <input type="checkbox" name="smsRemindersEnabled" defaultChecked={c.smsRemindersEnabled} className="mt-1" />
            <span>
              <span className="block text-sm font-medium">{t("settings.sms")}</span>
              <span className="block text-sm text-slate-500">{t("settings.sms_text")}</span>
            </span>
          </label>

          <h2 className="pt-2 font-semibold">{t("settings.booking_rules")}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label={t("settings.slot_interval")}
              name="slotIntervalMinutes"
              defaultValue={c.slotIntervalMinutes}
              error={fields.slotIntervalMinutes}
            >
              {SLOT_INTERVALS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
            <Field
              label={t("settings.min_notice")}
              name="minNoticeMinutes"
              type="number"
              min={0}
              max={10080}
              defaultValue={c.minNoticeMinutes}
              required
              error={fields.minNoticeMinutes}
            />
            <Field
              label={t("settings.max_advance")}
              name="maxAdvanceDays"
              type="number"
              min={1}
              max={365}
              defaultValue={c.maxAdvanceDays}
              required
              error={fields.maxAdvanceDays}
            />
            <Field
              label={t("settings.cancellation_notice")}
              name="cancellationNoticeMinutes"
              type="number"
              min={0}
              max={10080}
              defaultValue={c.cancellationNoticeMinutes}
              required
              error={fields.cancellationNoticeMinutes}
            />
          </div>
          <Button busy={save.isPending}>{t("settings.save")}</Button>
        </form>
      </Card>

      <Card>
        <h2 className="font-semibold">{t("settings.subscription")}</h2>
        <p className="mt-1 text-sm text-slate-600">{t("settings.sub_text")}</p>
        {c.subscription ? (
          <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-3">
            <dt className="text-slate-500">{t("settings.sub_plan")}</dt>
            <dd className="sm:col-span-2">{c.subscription.planName}</dd>
            <dt className="text-slate-500">{t("settings.sub_status")}</dt>
            <dd className="sm:col-span-2">
              <Badge tone={["active", "trialing"].includes(c.subscription.state) ? "green" : "amber"}>
                {t(`sub.state_${c.status === "suspended" ? "suspended" : c.subscription.state}`)}
              </Badge>
            </dd>
            <dt className="text-slate-500">{t("settings.sub_price")}</dt>
            <dd className="sm:col-span-2">
              {c.subscription.amountCents !== null
                ? `${formatMoney(locale, c.subscription.amountCents, c.subscription.currency)} ${t(`sub.per_${c.subscription.interval}`)}`
                : "–"}
            </dd>
            {c.subscription.trialEndsAt && c.subscription.status === "trialing" ? (
              <>
                <dt className="text-slate-500">{t("settings.sub_trial_ends")}</dt>
                <dd className="sm:col-span-2">{formatDate(locale, c.subscription.trialEndsAt, c.timezone)}</dd>
              </>
            ) : null}
            {c.subscription.currentPeriodEnd ? (
              <>
                <dt className="text-slate-500">{t("settings.sub_paid_until")}</dt>
                <dd className="sm:col-span-2">{formatDate(locale, c.subscription.currentPeriodEnd, c.timezone)}</dd>
              </>
            ) : null}
            <dt className="text-slate-500">{t("settings.sub_practitioners")}</dt>
            <dd className="sm:col-span-2">{c.subscription.maxPractitioners ?? t("settings.sub_unlimited")}</dd>
          </dl>
        ) : null}
      </Card>
    </div>
  );
}

export function ProfilePage() {
  const me = useRequiredMe();
  const { t, tError } = useT();
  const qc = useQueryClient();
  const rename = useMutation({
    mutationFn: (fullName: string) => api("PATCH", "/me", { fullName }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
  const changePassword = useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) => api("POST", "/me/password", input),
  });
  const pwFields = fieldErrors(changePassword.error);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t("profile.title")}</h1>
      <Card>
        <h2 className="font-semibold">{t("profile.details")}</h2>
        <form
          className="mt-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            rename.mutate(String(new FormData(e.currentTarget).get("fullName") ?? ""));
          }}
        >
          {rename.isSuccess ? <Alert tone="success">{t("common.saved")}</Alert> : null}
          {rename.error ? <Alert>{tError(rename.error)}</Alert> : null}
          <Field
            label={t("profile.name")}
            name="fullName"
            defaultValue={me.user.fullName}
            required
            autoComplete="name"
          />
          <Field label={t("profile.email")} value={me.user.email} disabled readOnly />
          <Button busy={rename.isPending}>{t("common.save")}</Button>
        </form>
      </Card>
      <Card>
        <h2 className="font-semibold">{t("profile.change_password")}</h2>
        <p className="mt-1 text-sm text-slate-600">{t("profile.change_password_text")}</p>
        <form
          className="mt-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            const form = e.currentTarget;
            const v = Object.fromEntries(new FormData(form)) as Record<string, string>;
            changePassword.mutate(
              { currentPassword: v.currentPassword ?? "", newPassword: v.newPassword ?? "" },
              { onSuccess: () => form.reset() },
            );
          }}
        >
          {changePassword.isSuccess ? <Alert tone="success">{t("profile.password_changed")}</Alert> : null}
          {changePassword.error && !pwFields.newPassword ? <Alert>{tError(changePassword.error)}</Alert> : null}
          <Field
            label={t("profile.current_password")}
            name="currentPassword"
            type="password"
            autoComplete="current-password"
            required
          />
          <Field
            label={t("profile.new_password")}
            name="newPassword"
            type="password"
            autoComplete="new-password"
            required
            error={pwFields.newPassword}
          />
          <Button busy={changePassword.isPending}>{t("auth.change_password")}</Button>
        </form>
      </Card>
    </div>
  );
}
